// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import {
  ArrowForwardOutlined,
  CheckOutlined,
  DeleteRounded,
} from '@/constants/hardfin-standard-icons';
import { ItemSearch } from '@/items/components/item-search';
import {
  ShipDialogFormData,
  ShipDialogItemError,
} from '@/shipment/components/outbound-ship-dialog';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import { SparseItem } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Control, Controller, UseFormStateReturn } from 'react-hook-form';

interface Props {
  index: number;
  item: ShipDialogFormData['items'][number];
  watchedItem: ShipDialogFormData['items'][number] | undefined;
  control: Control<ShipDialogFormData>;
  formState: UseFormStateReturn<ShipDialogFormData>;
  hiddenItems: SparseItem[];
  onRemove: (index: number) => void;
  onSerialDrawerOpen: (index: number | null) => void;
  serialDrawerOpen: number | null;
  onClearErrors: (index: number) => void;
  invalidSerials?: Set<string>;
  shipWarehouse: { id: string; name: string } | null;
  inventoryMap: Map<string, Map<string, number>>;
  isLoadingInventory: boolean;
  isFetchingInventory: boolean;
  hasAttemptedSubmit: boolean;
}

export function ShipDialogNewItem(props: Props) {
  const shouldRenderSerialFields = shipmentLineAllowsSerials(
    props.watchedItem?.item
  );

  const invalidSerialCount = props.invalidSerials?.size ?? 0;
  const itemError = props.formState.errors.items?.[props.index] as
    | ShipDialogItemError
    | undefined;
  const hasFormError = !!itemError?.serials;

  // Check for insufficient inventory (same logic as ShipDialogExistingItem)
  // Note: We check isLoadingInventory for UI display to avoid flickering,
  // but isFetchingInventory should also block validation to prevent race conditions
  // Bulk items (!hasAssets) require inventory validation
  const hasInsufficientInventory = (() => {
    if (
      !props.watchedItem?.item ||
      props.watchedItem.item.hasAssets || // Only check bulk items
      !props.shipWarehouse ||
      props.isLoadingInventory ||
      props.isFetchingInventory
    ) {
      return false;
    }

    const requestedQty = props.watchedItem.quantity ?? 0;
    const availableQty =
      props.inventoryMap
        .get(props.shipWarehouse.id)
        ?.get(props.watchedItem.item.id) ?? 0;

    return requestedQty > availableQty;
  })();

  const hasError =
    (props.hasAttemptedSubmit && invalidSerialCount > 0) ||
    (props.hasAttemptedSubmit && hasFormError) ||
    (props.hasAttemptedSubmit && hasInsufficientInventory);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: hasError ? 'error.main' : 'divider',
        borderWidth: hasError ? 2 : 1,
      }}
    >
      <Stack gap={2}>
        <Stack direction="row" gap={3}>
          {/* Left column - Item selector (60%) */}
          <Stack gap={1} sx={{ width: '60%' }}>
            <Controller
              control={props.control}
              name={`items.${props.index}`}
              render={({ field: itemField, fieldState }) => {
                const currentValue = itemField.value;
                // The item in the form is already a SparseItem
                const sparseItemValue = currentValue?.item ?? null;

                return (
                  <ItemSearch
                    label="Item"
                    value={sparseItemValue}
                    physicalOnly={true}
                    onChange={(value) => {
                      itemField.onChange({
                        item: value,
                        quantity: currentValue?.quantity ?? 1,
                        initialQuantity: currentValue?.initialQuantity ?? null,
                        externalReference:
                          currentValue?.externalReference ?? null,
                        serials: [],
                        isOriginalItem: false,
                      });
                    }}
                    hiddenItems={props.hiddenItems}
                    error={
                      props.hasAttemptedSubmit &&
                      !!(
                        fieldState.error as {
                          item?: { message?: string };
                        }
                      )?.item
                    }
                    helperText={
                      props.hasAttemptedSubmit
                        ? (
                            fieldState.error as {
                              item?: { message?: string };
                            }
                          )?.item?.message
                        : undefined
                    }
                  />
                );
              }}
            />
          </Stack>
          {/* Right column - Delete button and Shipped (40%) */}
          <Stack gap={1} sx={{ width: '40%' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              {shouldRenderSerialFields && (
                <Chip
                  label="serialized"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.75rem',
                    mt: 1,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => props.onRemove(props.index)}
                sx={{ ml: 'auto' }}
              >
                <DeleteRounded fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '70px' }}
              >
                Shipped
              </Typography>
              <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                <Box
                  sx={{
                    '& input': {
                      textAlign: 'right',
                    },
                    // Always hide helper text since we show inventory error separately
                    '& .MuiFormHelperText-root': {
                      display: 'none',
                    },
                    '& .MuiFormControl-root': {
                      marginBottom: 0,
                    },
                    // Conditionally hide error state if user hasn't attempted submit
                    ...(!props.hasAttemptedSubmit && {
                      '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                      '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                    }),
                  }}
                >
                  <FormNumberTextField
                    nullable
                    numeric
                    control={props.control}
                    name={`items.${props.index}.quantity`}
                    onChange={() => props.onClearErrors(props.index)}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 1.5 }}
                >
                  {(() => {
                    if (!props.watchedItem?.item) {
                      return '\u00A0'; // Non-breaking space to maintain height
                    }
                    if (!props.shipWarehouse) {
                      return 'Select shipping location first';
                    }
                    if (props.isLoadingInventory || props.isFetchingInventory) {
                      return 'Loading...';
                    }
                    const availableQty =
                      props.inventoryMap
                        .get(props.shipWarehouse.id)
                        ?.get(props.watchedItem.item.id) ?? 0;
                    return `${availableQty} available`;
                  })()}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center">
          <Box sx={{ width: '60%' }}>
            <Box sx={{ width: '70%' }}>
              <FormTextField
                fullWidth
                label="Reference ID (optional)"
                placeholder="Ex: batch number"
                control={props.control}
                name={`items.${props.index}.externalReference`}
              />
            </Box>
          </Box>
          <Box
            sx={{
              width: '40%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              minHeight: '56px',
            }}
          >
            {props.hasAttemptedSubmit &&
              hasInsufficientInventory &&
              props.watchedItem?.item &&
              props.shipWarehouse && (
                <Typography
                  variant="body2"
                  color="error.main"
                  sx={{ textAlign: 'right' }}
                >
                  Insufficient inventory
                </Typography>
              )}
            {shouldRenderSerialFields &&
              !hasInsufficientInventory &&
              (() => {
                const serialCount = props.watchedItem?.serials?.length || 0;
                const expectedQuantity = props.watchedItem?.quantity || 0;
                const hasError = !!itemError?.serials;

                let text = '';
                let icon = null;
                let color = 'text.secondary';

                if (invalidSerialCount > 0) {
                  // Show invalid serials count
                  text = `${invalidSerialCount} out of ${serialCount} ${pluralize(
                    serialCount,
                    {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: false,
                    }
                  )} ${invalidSerialCount === 1 ? 'is' : 'are'} invalid`;
                  color = 'error.main';
                } else if (serialCount === 0 && expectedQuantity === 0) {
                  text = 'Complete';
                  icon = <CheckOutlined fontSize="small" />;
                  color = 'success.main';
                } else if (serialCount === 0) {
                  text = 'No serials entered';
                  icon = <ArrowForwardOutlined fontSize="small" />;
                  color =
                    hasError && props.hasAttemptedSubmit
                      ? 'error.main'
                      : 'text.secondary';
                } else if (serialCount === expectedQuantity) {
                  text = `${formatNumber(serialCount)} serials entered`;
                  icon = <CheckOutlined fontSize="small" />;
                  color = 'success.main';
                } else if (serialCount > expectedQuantity) {
                  const extra = serialCount - expectedQuantity;
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered (${formatNumber(extra)} extra)`;
                  color = 'error.main';
                } else {
                  const needed = expectedQuantity - serialCount;
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered (${formatNumber(needed)} needed)`;
                  color =
                    hasError && props.hasAttemptedSubmit
                      ? 'error.main'
                      : props.hasAttemptedSubmit
                        ? 'warning.dark'
                        : 'text.secondary';
                }

                return (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() =>
                      props.onSerialDrawerOpen(
                        props.serialDrawerOpen === props.index
                          ? null
                          : props.index
                      )
                    }
                    sx={{
                      textAlign: 'right',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      width: '100%',
                      cursor: 'pointer',
                      color: color,
                    }}
                  >
                    {text}
                    {icon}
                  </Link>
                );
              })()}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
